<template lang="pug">
template(v-if="!pullingServices")
    .bq-baskets-empty(v-if="isFirstService")
        .bq-empty-inner
            BunnyLoader.bq-empty-bunny(:overlay="false")
            h2 Welcome!#[br]Create your first project
            p.bq-empty-desc Give your project a name to get started. #[span.wordset Add a base prompt] to give the AI assistant context about your project.
            form.bq-empty-form(@submit.prevent="createFirstProject")
                label.bq-field-label(for="firstProjectName") Project name
                input#firstProjectName.bq-input(
                    :placeholder="projectNamePlaceholder"
                    maxlength="256"
                    required
                    :value="newServiceName"
                    @input="newServiceName = $event.target.value"
                    :disabled="promiseRunning"
                    @keydown.enter.prevent="firstDescriptionRef?.focus()"
                )
                label.bq-field-label(for="firstProjectDescription") Base Prompt
                textarea#firstProjectDescription.bq-textarea(
                    ref="firstDescriptionRef"
                    :placeholder="projectDescriptionPlaceholder"
                    rows="5"
                    :value="newServiceDescription"
                    @input="newServiceDescription = $event.target.value"
                    :disabled="promiseRunning"
                )
                button.btn(
                    type="submit"
                    data-track="serviceList_createService_submit"
                    :disabled="!newServiceName"
                    :class="{ 'bq-btn--loading': promiseRunning }"
                )
                    span.bq-loader(v-if="promiseRunning")
                    template(v-else) + Create
                .error(v-if="error" style="margin-top: 0.5rem")
                    span {{ error }}

    .bq-baskets(v-else)
        EmailVerifyBanner
        NewsBanner
        .bq-baskets-header
            button.bq-btn(@click="showCreateModal = true" data-track="serviceList_openCreateService") + New Project

        .bq-table-wrap
            table.bq-table
                thead
                    tr
                        th Name
                        th State
                        th Plan
                        th Prompt
                tbody
                    template(v-if="!serviceListComputed.length")
                        tr.bq-row-loading
                            td(colspan="4")
                                .loader

                    template(v-else)
                        template(v-for="service of serviceListComputed")
                            tr.bq-row(
                                v-if="isResolvedService(service)"
                                @click="goServiceDashboard(service)"
                                :class="{ 'bq-row--dim': service.formattedSpecs.serviceStatus.statusCode < 0 }"
                            )
                                td.bq-cell-name(translate="no") {{ service.formattedSpecs.serviceStatus.name }}
                                td.bq-cell-state
                                    template(v-if="!service.service?.will_terminate && service.subscription?.will_cancel")
                                        span.bq-state.bq-state--cancel Cancels {{ service.subscription?.will_cancel }}
                                    template(v-else)
                                        span.bq-state(
                                            :class="'bq-state--' + service.formattedSpecs.serviceStatus.status.toLowerCase()"
                                        ) {{ service.formattedSpecs.serviceStatus.status === "Suspended" ? "Deletes " + service.service?.will_terminate : service.formattedSpecs.serviceStatus.status }}
                                td.bq-cell-plan
                                    span.bq-badge(
                                        :class="service.formattedSpecs.currentPlan.label.toLowerCase().split(' ')[0]"
                                    ) {{ service.formattedSpecs.currentPlan.label.split(" ")[0] }}
                                td.bq-cell-desc(translate="no") {{ service.service?.description || '—' }}
                            tr.bq-row-loading(v-else)
                                td.bq-cell-skeleton(colspan="4")
        .bq-loading-more(v-if="pullingMoreServices")
            .loader

        .bq-load-more-sentinel(
            v-if="!endOfList"
            ref="loadMoreSentinel"
            aria-hidden="true"
        )

    CreateProject(
        @close="showCreateModal = false"
        :showCreateModal="showCreateModal"
    )
</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";
import {
    serviceList,
    serviceIdList,
    pullServices,
    endOfList,
    addServiceToList,
    pickProjectPlaceholders,
} from "@/views/project-list";
import { type ServiceFromServer, createService } from "@/code/service";

import CreateProject from "@/views/project-create.vue";
import EmailVerifyBanner from "@/components/emailVerifyBanner.vue";
import NewsBanner from "@/components/newsBanner.vue";
import BunnyLoader from "@/components/bunnyLoader.vue";
import { removeFullScreenLoader } from "@/components/jsComponents";

const router = useRouter();
const route = useRoute();

let showCreateModal = ref(false);
let newServiceName = ref("");
let newServiceDescription = ref("");
let firstDescriptionRef = ref();
let promiseRunning = ref(false);
let error = ref("");

const validateServiceName = (name: string): boolean => {
    let regex = /[<>`]/g;
    return !name.match(regex);
};

let createFirstProject = async () => {
    // Re-entry guard: a fast double submit (e.g. holding Enter) can fire again
    // before the disabled/loading DOM state repaints — swallow it so the create
    // request goes out only once. Mirrors createProject in project-create.vue.
    if (promiseRunning.value) return;
    error.value = "";

    if (!validateServiceName(newServiceName.value)) {
        error.value = "Angle brackets and backticks are not allowed";
        return;
    }

    promiseRunning.value = true;

    let params: { name: string; description?: string } = { name: newServiceName.value };
    if (newServiceDescription.value.trim()) {
        params.description = newServiceDescription.value.trim();
    }

    let service = await createService(params).catch((err) => {
        error.value = err instanceof Error ? err.message : String(err);
        promiseRunning.value = false;
    });

    if (!service) return;

    addServiceToList([service as ServiceFromServer]).then(() => {
        promiseRunning.value = false;
        newServiceName.value = "";
        newServiceDescription.value = "";
        router.push(`/my-projects/${(service as ServiceFromServer).service}/agent`);
    });
};

let pullingServices = ref(true);
let pullingMoreServices = ref(false);
let loadMoreSentinel = ref<HTMLElement | null>(null);
let loadMoreObserver: IntersectionObserver | null = null;

const projectPlaceholders = pickProjectPlaceholders();
const projectNamePlaceholder = projectPlaceholders.name;
const projectDescriptionPlaceholder = projectPlaceholders.description;

const pullNextPage = () => {
    if (pullingMoreServices.value || endOfList.value) return;

    pullingMoreServices.value = true;
    pullServices().finally(() => {
        pullingMoreServices.value = false;
    });
};

let threeStrikes = 0;

onMounted(async () => {
    // console.log("Service List mounted with query:", route.query);

    if (endOfList.value) {
        pullingServices.value = false;
        removeFullScreenLoader();
        return;
    }

    while (
        serviceIdList.value.length <= 25 &&
        !endOfList.value &&
        threeStrikes < 3
    ) {
        try {
            await pullServices();
            pullingServices.value = false;
            removeFullScreenLoader();
        } catch (err) {
            console.error("Error while pulling services:", err);
            threeStrikes++;
        }

        // console.log(
        //     `Pulled services, count: ${serviceIdList.value.length}, endOfList: ${endOfList.value}, threeStrikes: ${threeStrikes}`
        // );
    }

    // Guarantee the loader/content is never stuck. The removals above only fire on
    // the endOfList fast-path or a SUCCESSFUL in-loop pull, so the loop can exit
    // with pullingServices still true — e.g. returning to the list after visiting a
    // project when >25 services are already loaded (serviceIdList.length > 25 skips
    // the loop body while endOfList is still false), or when every pull attempt
    // failed. Content is gated behind v-if="!pullingServices", so always reveal it.
    pullingServices.value = false;
    removeFullScreenLoader();

    await nextTick();

    if (!endOfList.value && loadMoreSentinel.value) {
        loadMoreObserver = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    pullNextPage();
                }
            },
            {
                rootMargin: "0px 0px 300px 0px",
            }
        );

        loadMoreObserver.observe(loadMoreSentinel.value);
    }
});

let serviceListComputed = computed(() => {
    return serviceIdList.value.map((id) => serviceList[id]);
});

const isResolvedService = (service: any) => {
    return !(service instanceof Promise);
};

onUnmounted(() => {
    loadMoreObserver?.disconnect();
    loadMoreObserver = null;
});

const isFirstService = computed(() => {
    return serviceIdList.value.length === 0;
});

let goServiceDashboard = (service: any) => {
    const id = service.formattedSpecs.serviceStatus.original_service_id;
    router.push(`/my-projects/${id}/agent`);
};
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";

/* ─── empty state ─── */
.bq-baskets-empty {
    // min-height: calc(100vh - 4rem);
    display: flex;
    align-items: center;
    justify-content: center;
    // padding: 5rem 1.5rem 5rem;
    padding: 5rem 1.5rem 3rem;
    font-family: @mono;
    color: @ink;
    overflow: hidden;
}

.bq-load-more-sentinel {
    width: 100%;
    height: 1px;
}

.bq-empty-inner {
    // text-align: center;
    max-width: 28rem;
}

.bq-empty-bunny {
    margin-bottom: 2rem;
}

.bq-empty-inner h2 {
    margin: 0 0 0.75rem;
    font-size: 1.5rem;
    font-weight: 600;
}

.bq-empty-desc {
    margin: 0 0 2rem;
    color: @muted;
    font-size: 0.92rem;
    line-height: 1.6;
}

.bq-empty-form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.bq-field-label {
    font-size: 0.78rem;
    font-weight: 600;
    color: @ink;
    margin-bottom: -0.35rem;
    letter-spacing: 0.02em;
}

.bq-input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid @line;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 0.9rem;
    outline: none;
    transition: border-color 0.15s;

    &::placeholder {
        color: @placeholder;
    }

    &:focus {
        border-color: @ink;
    }
}

.bq-textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid @line;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 0.9rem;
    outline: none;
    transition: border-color 0.15s;
    resize: vertical;
    min-height: 4rem;
    word-wrap: break-word;
    overflow-x: hidden;
    white-space: pre-wrap;

    &::placeholder {
        color: @placeholder;
    }

    &:focus {
        border-color: @ink;
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
}

.bq-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem 1.4rem;
    border: 1px solid @ink;
    background: @ink;
    color: @paper;
    font-family: @mono;
    font-size: 0.9rem;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    width: auto;
    max-width: none;
    border-radius: 0;
    box-shadow: 2px 2px rgba(0, 0, 0, 0.12);

    &:hover {
        background: @paper;
        color: @ink;
    }

    &:disabled {
        opacity: 0.3;
        cursor: default;
        pointer-events: none;
    }

    &::after {
        display: none;
    }
}

/* ─── basket list ─── */
.bq-baskets {
    max-width: 1200px;
    margin: 0 auto;
    padding: 3rem;
    font-family: @mono;
    color: @ink;
}

.bq-baskets-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2rem;
    margin-top: 2rem;

    h1 {
        margin: 0;
        font-size: 1.5rem;
        font-weight: 600;
    }
}

/* ─── table ─── */
.bq-table-wrap {
    overflow: hidden;
    border: 1px solid @line;
}

.bq-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    font-size: 0.85rem;

    thead {
        border-bottom: 1px solid @line;

        th {
            padding: 0.75rem 1rem;
            text-align: left;
            font-weight: 600;
            font-size: 0.78rem;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: @muted;
            white-space: nowrap;
        }
    }

    tbody {
        tr.bq-row {
            border-bottom: 1px solid @hover-bg;
            cursor: pointer;
            transition: background 0.1s;

            &:hover {
                background: @hover-bg;
            }

            &:last-child {
                border-bottom: none;
            }
            
            &:first-child {
                border-bottom: 1px solid @hover-bg;
            }

            &.bq-row--dim > *:not(.bq-cell-sub) {
                opacity: 0.4;
            }
        }

        td {
            padding: 0.85rem 1rem;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 14rem;
        }
    }
}

.bq-cell-name {
    font-weight: 500;
}

.bq-cell-id {
    font-size: 0.7rem;
    color: @muted;
}

.bq-cell-desc {
    color: @muted;
    font-size: 0.8rem;
    max-width: 20rem;
}

/* state dots */
.bq-state {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.82rem;

    &::before {
        content: "";
        display: inline-block;
        width: 0.5rem;
        height: 0.5rem;
        border-radius: 50%;
        background: #5ad858;
        flex-shrink: 0;
    }

    &.bq-state--disabled::before {
        background: @placeholder;
    }

    &.bq-state--suspended::before,
    &.bq-state--cancel::before {
        background: var(--caution-color, #f04e4e);
    }
}

/* plan badges — global in main.less */

.bq-host-link {
    color: @ink;
    text-decoration: none;
    font-size: 1rem;

    &:hover {
        text-decoration: underline;
    }
}

.bq-upgrade {
    display: inline-block;
    padding: 0.25rem 0.6rem;
    border: 1px solid @ink;
    font-size: 0.75rem;
    color: @paper;
    background: @ink;
    text-decoration: none;
    transition: background 0.15s, color 0.15s;

    &:hover {
        background: @paper;
        color: @ink;
        text-decoration: none;
    }
}

.bq-cell-hosting {
    text-align: center;
}

.bq-cell-sub {
    text-align: center;
}

/* loading rows */
.bq-row-loading td {
    padding: 1.25rem 1rem;
    text-align: center;
}

.bq-cell-skeleton {
    position: relative;
    &::before {
        content: "";
        display: block;
        width: 100%;
        height: 1.5rem;
        background: linear-gradient(90deg, transparent 40%, #e0e0e0 50%, transparent 60%);
        background-size: 200% 100%;
        animation: bq-shimmer 1.2s ease-in-out infinite;
    }
}

@keyframes bq-shimmer {
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
}

.bq-loading-more {
    padding: 2rem 0;
    text-align: center;
}

/* ─── responsive ─── */
@media (max-width: 800px) {
    .bq-baskets-empty {
        padding: 1.5rem 1.5rem 3rem;
    }

    .bq-empty-bunny {
        display: none;
    }

    .bq-baskets {
        padding: 2rem 1.25rem;
    }

    .bq-baskets-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }

    .bq-table-wrap {
        border: none;
        overflow: hidden;
    }

    .bq-table {
        min-width: 0;
        table-layout: fixed;
        width: 100%;

        thead {
            display: none;
        }

        tbody {
            tr.bq-row {
                display: flex;
                align-items: center;
                padding: 0.85rem 0.75rem;
                gap: 0;
                border: 1px solid @line;
                margin-bottom: 0.5rem;
                transition: background 0.1s;

                // chevron arrow
                &::after {
                    content: "\203A";
                    flex-shrink: 0;
                    font-size: 1.4rem;
                    color: @muted;
                    margin-left: auto;
                    padding-left: 0.5rem;
                    line-height: 1;
                }

                &:active {
                    background: @hover-bg;
                }

                &:last-child {
                    border-bottom: 1px solid @line;
                }

                &:first-child {
                    border-bottom: 1px solid @line;
                }
            }

            td {
                display: none;
                padding: 0;
                max-width: none;
            }

            td.bq-cell-state {
                display: block;
                overflow: visible;
                order: -1;
                flex-shrink: 0;

                .bq-state {
                    font-size: 0;
                    gap: 0;

                    &::before {
                        width: 0.5rem;
                        height: 0.5rem;
                    }
                }
            }

            td.bq-cell-name {
                display: block;
                padding-left: 0.6rem;
                font-size: 0.9rem;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                min-width: 0;
                flex: 1;
            }

            td.bq-cell-plan {
                display: block;
                flex-shrink: 0;
                padding-left: 0.5rem;
            }
        }
    }

    .bq-row-loading td {
        display: block !important;
    }

    .bq-cell-skeleton {
        display: block !important;
        margin-bottom: 0.5rem;
    }
}
</style>
